import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import { updateProductMetaData } from "@containers/App/reducer.js"
import {
    parseWidgetData,
    parseSlotSelectionData,
    parseCenterDetails,
    parsePatientFormFields,
    findTimeSlotById,
} from "./parser"
import { getCookie } from "@commonUtils"
import { genericErrorMessage } from "./constants"

const initialState = {
    isLoading: false,
    // Adding this flag as we use same api to fetch data of multiple pages but will show skeleton on initial loading only
    initialFetched: false,
    error: null,
    formData: {},
    snackBarError: "",
    dialogError: "",
    // This is used in disabling/enabling the continue button
    allowNextStep: true,
    testLocationDetails: {
        // These are objects because these store two states (radiology and pathology)
        data: {},
        isLoading: {},
        isFetched: {},
        error: {},
    },
    slotsSelection: {
        // These are objects because these store two states (radiology and pathology)
        data: {},
        isLoading: {},
        isFetched: {},
        error: {},
    },
    labsBookingPageData: {
        bottomSheets: {},
        feasibility: {
            success: null,
        },
    },
    confirmBooking: {
        isLoading: false,
        error: null,
        data: null,
    },
    cities: {
        isFetched: false,
        error: null,
        isFetching: false,
    },
    currentLocationCity: {
        isFetched: false,
        error: null,
        isFetching: false,
    },
    patientAddEdit: {
        isLoading: false,
        isFetched: false,
        error: null,
        data: null,
    },
}

export const corporateLabsBookingPageSlice = createSlice({
    name: "corporateLabsBookingPageReducer",
    initialState: initialState,
    reducers: {
        setFormData(state, action) {
            state.formData = { ...state.formData, ...action.payload }
        },
        setCurrentStep(state, action) {
            state.labsBookingPageData.currentStep = action.payload
        },
        clearSectionData(state, action) {
            action.payload.forEach((sectionName) => {
                delete state.labsBookingPageData.widgets?.bookingFormSection?.[sectionName]
            })
        },
        clearSlotSelection(state, action) {
            state.slotsSelection.data[action.payload] = null
            state.slotsSelection.error[action.payload] = null
            state.slotsSelection.isLoading[action.payload] = false
            state.slotsSelection.isFetched[action.payload] = false
        },
        resetForm(state) {
            state = { ...initialState }
            return state
        },
        resetFeasbillity(state) {
            state.labsBookingPageData.feasibility.success = null
        },
        setFormError: (state, action) => {
            state.confirmBooking.error = action.payload
        },
        setSnackBarError: (state, action) => {
            state.snackBarError = action.payload
        },
        setDialogError: (state, action) => {
            state.dialogError = action.payload
        },
        resetTestLocation(state, action) {
            state.formData[action.payload] = {
                ...state.formData[action.payload],
                selectedTestLocation: {
                    ...state.formData[action.payload].selectedTestLocation,
                    value: "",
                    address: "",
                    displayValue: "",
                    labId: "",
                },
                patientCollectionSlot: {
                    value: "",
                    isDisabled: true,
                    isVisible: true,
                },
            }
        },
        cleanPageError: (state) => {
            state.error = null
        },
        setAllowNextStep: (state, action) => {
            state.allowNextStep = action.payload
        },
    },
    extraReducers: (builder) => {
        //handle asynchronous actions
        // Fetch Booking page data actions
        builder.addCase(fetchLabsBookingData.fulfilled, (state, action) => {
            const { formData, ...labsBookingPageData } = parseWidgetData(
                action?.payload,
                state.labsBookingPageData,
                state.formData
            )
            state.labsBookingPageData = labsBookingPageData
            state.formData = formData
            state.initialFormState = formData
            state.isFetched = true
            state.isLoading = false
            state.error = false
            state.initialFetched = true
        }),
            builder.addCase(fetchLabsBookingData.pending, (state) => {
                state.isLoading = true
                state.error = false
            }),
            builder.addCase(fetchLabsBookingData.rejected, (state, action) => {
                state.isLoading = false
                state.error = action.payload
            })

        builder.addCase(fetchCities.fulfilled, (state, action) => {
            state.labsBookingPageData.bottomSheets.selectCity = {
                ...state.labsBookingPageData?.bottomSheets?.selectCity,
                topCities: {
                    data: action.payload?.top_cities?.data,
                    label: action.payload?.top_cities?.display_text,
                    defaultCity: action.payload?.top_cities?.default_city,
                },
                cities: {
                    data: action.payload?.all_cities?.data,
                    label: action.payload?.all_cities?.display_text,
                },
                popUp: action.payload?.popups,
                gaDetails: action?.payload?.ga_details || {},
                onChangeGaDetails: action?.payload?.on_change_ga_details || {},
            }
            state.cities.isFetched = true
            state.cities.isFetching = false
            state.cities.error = null
        }),
            builder.addCase(fetchCities.pending, (state) => {
                state.cities.isFetched = false
                state.cities.isFetching = true
                state.cities.error = null
            }),
            builder.addCase(fetchCities.rejected, (state, action) => {
                state.cities.isFetching = false
                state.cities.isFetched = false
                state.cities.error = action.payload
            }),
            builder.addCase(getCurrentLocationCity.fulfilled, (state, action) => {
                const newCity = action.payload?.result[0]?.city
                const currentCity = {
                    currentCity: newCity,
                }
                state.formData.patientCity = { ...state.formData.patientCity, ...currentCity }
                state.currentLocationCity.isFetching = false
                state.currentLocationCity.isFetched = true
                state.currentLocationCity.error = null
            }),
            builder.addCase(getCurrentLocationCity.pending, (state) => {
                state.currentLocationCity.isFetched = false
                state.currentLocationCity.isFetching = true
                state.currentLocationCity.error = null
            }),
            builder.addCase(getCurrentLocationCity.rejected, (state, action) => {
                state.currentLocationCity.error = action.payload
                state.currentLocationCity.isFetching = false
                state.currentLocationCity.isFetching = false
            })
        builder.addCase(createCart.fulfilled, (state, action) => {
            const { navigate, successUrl } = action.meta.arg
            navigate(successUrl)
        }),
            builder.addCase(createCartSku.fulfilled, (state, action) => {
                const { navigate, successUrl } = action.meta.arg
                navigate(successUrl)
            }),
            // Fetch Time Slots actions
            builder.addCase(fetchSlotsData.fulfilled, (state, action) => {
                const { testType } = action.meta.arg
                const { bottomSheets, popups } = parseSlotSelectionData(action?.payload)
                state.labsBookingPageData.popups = { ...state.labsBookingPageData.popups, ...popups }
                state.slotsSelection.data[testType] = bottomSheets
                if (Object.keys(state.slotsSelection.data?.[testType]?.slotSelector?.slots).length === 0) {
                    state.formData[testType].patientCollectionSlot.error = true
                    state.formData[testType].patientCollectionSlot.isDisabled = true
                } else {
                    state.formData[testType].patientCollectionSlot.error = false
                    state.formData[testType].patientCollectionSlot.isDisabled = false
                    const selectedSlotId = state.formData?.[testType]?.patientCollectionSlot?.value
                    const selectedSlot = findTimeSlotById(bottomSheets.slotSelector?.slots, selectedSlotId)
                    if (selectedSlot && selectedSlotId) {
                        state.formData[testType].patientCollectionSlot.time = selectedSlot
                    }
                }
                state.slotsSelection.isLoading[testType] = false
                state.slotsSelection.error[testType] = null
                state.slotsSelection.isFetched[testType] = true
                if (action.payload.locationType === "home") {
                    state.formData[testType].selectedTestLocation.labId = action.payload.labHomeId
                }
            }),
            builder.addCase(fetchSlotsData.pending, (state, action) => {
                const { testType } = action.meta.arg
                state.slotsSelection.isLoading[testType] = true
            }),
            builder.addCase(fetchSlotsData.rejected, (state, action) => {
                const { testType } = action.meta.arg
                state.slotsSelection.isLoading[testType] = false
                state.slotsSelection.error[testType] = action.payload
                state.slotsSelection.data[testType] = null
                state.slotsSelection.isFetched[testType] = true
            })
        // Confirm Booking actions
        builder.addCase(confirmBooking.fulfilled, (state, action) => {
            action.meta.arg.navigate(
                `/corporate/labs/summary?orderId=${action?.payload?.order_id.join(",")}&benefitId=${
                    action?.payload?.benefit_id
                }`
            )
        }),
            builder.addCase(confirmBooking.pending, (state) => {
                state.confirmBooking.isLoading = true
            }),
            builder.addCase(confirmBooking.rejected, (state, action) => {
                state.confirmBooking = { isLoading: false, data: null, error: action.payload }
            })

        // fetch camp center actions
        builder.addCase(fetchCenterDetails.fulfilled, (state, action) => {
            const parsedData = parseCenterDetails(action?.payload)
            const { testType } = action.meta.arg
            const payload = {
                ...state.formData[testType].selectedTestLocation,
                isDisabled: false,
                isVisible: true,
                error: false,
            }
            state.formData[testType].selectedTestLocation = payload
            state.testLocationDetails.data[testType] = parsedData
            state.testLocationDetails.isLoading[testType] = false
            state.testLocationDetails.isFetched[testType] = true
            state.testLocationDetails.error[testType] = null
        })
        builder.addCase(fetchCenterDetails.pending, (state, action) => {
            const { testType } = action.meta.arg
            state.testLocationDetails.isLoading[testType] = true
            state.testLocationDetails.error[testType] = false
        })
        builder.addCase(fetchCenterDetails.rejected, (state, action) => {
            const { testType } = action.meta.arg
            state.testLocationDetails.isLoading[testType] = false
            state.testLocationDetails.isFetched[testType] = true
            state.testLocationDetails.error[testType] = action.payload

            state.formData[testType].selectedTestLocation = {
                ...state.formData[testType].selectedTestLocation,
                isDisabled: true,
                value: "",
                isVisible: true,
                error: true,
            }

            state.formData[testType].patientCollectionSlot = {
                ...state.initialFormState[testType].patientCollectionSlot,
                isDisabled: true,
            }
        })
        builder.addCase(fetchCorporateLabsReviewData.fulfilled, (state, action) => {
            // eslint-disable-next-line no-unused-vars
            const { formData, ...labsBookingPageData } = parseWidgetData(
                action?.payload.data,
                state.labsBookingPageData
            )
            state.labsBookingPageData = labsBookingPageData
            state.isFetched = true
            state.isLoading = false
            state.error = false
        }),
            builder.addCase(fetchCorporateLabsReviewData.pending, (state) => {
                state.isLoading = true
                state.error = false
            }),
            builder.addCase(fetchCorporateLabsReviewData.rejected, (state, action) => {
                state.isLoading = false
                state.error = action.payload
            })
        builder.addCase(managePatient.fulfilled, (state, action) => {
            const { bottomSheets } = state.labsBookingPageData
            const { patientDetails } = bottomSheets
            state.labsBookingPageData = {
                ...state.labsBookingPageData,
                bottomSheets: {
                    ...bottomSheets,
                    patientDetails: {
                        ...patientDetails,
                        ctaData: {
                            ...patientDetails.ctaData,
                            functionMap:
                                action?.payload?.dynamicCtaField.bottom_sheet_data?.cta?.function_map,
                        },
                        addPatientData: {
                            ...patientDetails.addPatientData,
                            formFields: parsePatientFormFields(
                                action?.payload?.dynamicCtaField.bottom_sheet_data,
                                patientDetails
                            ),
                        },
                    },
                },
            }
            state.formData.patientName = {
                ...state.formData.patientName,
                displayValue: action?.payload?.patient_name,
                value: action?.payload?.patient_id,
                id: action?.payload?.patient_id,
                image: action.payload?.dynamicCtaField?.bottom_sheet_data?.fields?.[0]?.image,
            }
            // Modify cta label
            const patientSection = state.labsBookingPageData.widgets.bookingFormSection.personal_info
            const patientField = patientSection.form.fields.find((field) => field.name === "patientName")
            if (patientField) {
                patientField.cta = { ...patientField.cta, changeLabel: "View Details" }
            }
            state.patientAddEdit.isLoading = false
            state.patientAddEdit.isFetched = true
        }),
            builder.addCase(managePatient.pending, (state) => {
                state.patientAddEdit.isLoading = true
                state.patientAddEdit.error = ""
            }),
            builder.addCase(managePatient.rejected, (state, action) => {
                state.patientAddEdit.isLoading = false
                state.patientAddEdit.error = action.payload
            })
        builder.addCase(checkFeasibility.fulfilled, (state, action) => {
            const { is_success, packageId } = action.payload
            if (is_success) {
                state.labsBookingPageData.feasibility.success = true
            } else {
                state.labsBookingPageData.bottomSheets.selectLabTest.listOfTests =
                    state.labsBookingPageData.bottomSheets.selectLabTest.listOfTests.map((test) => {
                        if (test.id === packageId) {
                            return { ...test, isTestFeasible: false }
                        }
                        return test
                    })
                state.labsBookingPageData.feasibility.success = false
            }
        })

        // handle preference api call
        builder.addCase(cartPreference.pending, (state) => {
            state.allowNextStep = false
        }),
            builder.addCase(cartPreference.rejected, (state) => {
                state.allowNextStep = false
            })
    },
})

/**
 * @description Fetch Page Data for corporate Lab Test booking page
 */
export const fetchLabsBookingData = createAsyncThunk(
    "corporateLabsBookingPage/fetchData",
    async (_arg, { extra, dispatch, rejectWithValue, getState }) => {
        const { step, params = "" } = _arg

        const { corporateDetails, userData } = getState().shellReducer
        const { api } = extra
        try {
            if (!corporateDetails?._source) {
                throw new Error("Source is missing")
            }
            const res = await api.get(
                `/api/rbf/corporate/${corporateDetails?._source}/booking-page-data?section_name=${step}${params}`
            )
            if (res?.data?.meta_data) {
                dispatch(
                    updateProductMetaData({
                        title: res.data.meta_data?.title,
                        description: res.data.meta_data?.description,
                    })
                )
            }
            res.data.corporateDetails = corporateDetails
            res.data.userData = userData
            return Promise.resolve(res.data)
        } catch (err) {
            return rejectWithValue(err?.message || err?.error?.message || true)
        }
    }
)

/**
 * @description Fetch camp/center details with given location and test type
 */
export const fetchCenterDetails = createAsyncThunk(
    "corporateLabsBookingPage/testing",
    async (_arg, { extra, rejectWithValue, getState }) => {
        const { testType, locationType } = _arg
        const { api } = extra
        const { corporateDetails: { _source: source } = {} } = getState().shellReducer
        try {
            const res = await api.get(
                `api/rbf/corporate/${source}/test/collection-centres?test_type=${testType}&location_type=${locationType}`
            )
            return Promise.resolve(res.data)
        } catch (err) {
            return rejectWithValue(err.message)
        }
    }
)

/**
 * @description Fetch slot data for lab test and center
 */
export const fetchSlotsData = createAsyncThunk(
    "corporateLabsBookingPage/fetchSlots",
    async (_arg, { extra, rejectWithValue, getState }) => {
        const { corporateDetails: { _source: source } = {} } = getState().shellReducer
        const { testType } = _arg

        const { api } = extra

        try {
            const res = await api.get(
                `/api/rbf/corporate/${source}/lab/date-time-slot?product_category=${testType}`
            )
            return Promise.resolve(res.data)
        } catch (err) {
            return rejectWithValue(err.message)
        }
    }
)

export const fetchCorporateLabsReviewData = createAsyncThunk(
    "corporateLabsBookingPage/review",
    async (_arg, { extra, rejectWithValue, getState }) => {
        const {
            formData: {
                pathology,
                patientLabTest,
                patientCity,
                patientName,
                radiology,
                isPathologyTestBooked,
                isRadiologyTestBooked,
            },
        } = getState().corporateLabsBookingPageReducer
        const { api } = extra
        const { corporateDetails: { _source: source } = {} } = getState().shellReducer
        try {
            const payload = {
                package_id: patientLabTest?.value,
                city: patientCity?.displayValue,
                patient_id: patientName?.value,
            }
            if (pathology) {
                payload.pathology = !isPathologyTestBooked
                    ? {
                          test_location: {
                              id: pathology?.patientTestLocation?.id,
                              value: pathology?.patientTestLocation?.value,
                          },
                          sample_collection_address: {
                              id: pathology?.selectedTestLocation?.id,
                              value: pathology?.selectedTestLocation?.displayValue,
                          },
                          sample_collection_slot: {
                              id: pathology?.patientCollectionSlot?.id,
                              value: pathology?.patientCollectionSlot?.displayValue,
                          },
                          instructions: {
                              value: pathology?.patientCollectionSlot?.testInstructions?.instructions,
                              background_color:
                                  pathology?.patientCollectionSlot?.testInstructions?.backgroundColor,
                          },
                      }
                    : null
            }
            if (radiology) {
                payload.radiology = !isRadiologyTestBooked
                    ? {
                          test_location: radiology?.patientTestLocation || {
                              id: radiology?.patientTestLocation?.id,
                              value: radiology?.patientTestLocation?.value,
                          },
                          sample_collection_address: {
                              id: radiology?.selectedTestLocation?.id,
                              value: radiology?.selectedTestLocation?.displayValue,
                          },
                          sample_collection_slot: {
                              id: radiology?.patientCollectionSlot?.value,
                              value: radiology?.patientCollectionSlot?.displayValue,
                          },
                          instructions: {
                              value: radiology?.patientCollectionSlot?.testInstructions?.instructions,
                              background_color:
                                  radiology?.patientCollectionSlot?.testInstructions?.backgroundColor,
                          },
                      }
                    : null
            }
            const res = await api.get(`/api/rbf/corporate/${source}/order/review-detail`)
            return Promise.resolve(res.data)
        } catch (error) {
            if (error.error.redirect_url) {
                window.location.replace(error.error.redirect_url)
                return
            }
            return rejectWithValue(error?.message || genericErrorMessage)
        }
    }
)

/**
 * @description Confirm Lab Test booking thunk
 */
export const confirmBooking = createAsyncThunk(
    "corporateLabsBookingPage/confirmBooking",
    async (_arg, { extra, rejectWithValue, getState, dispatch }) => {
        try {
            const { corporateDetails: { _source: source } = {} } = getState().shellReducer
            const { api } = extra
            const { patientId } = _arg
            const res = await api.post(`/api/rbf/corporate/${source}/booking`, { patient_id: patientId })
            if (res.data) {
                dispatch(resetForm())
                return Promise.resolve(res.data)
            }
            return rejectWithValue(genericErrorMessage)
        } catch (error) {
            return rejectWithValue(error?.error?.message || genericErrorMessage)
        }
    }
)

export const getCurrentLocationCity = createAsyncThunk(
    "locationPicker/getCurrentLocationCity",
    async ({ lat, long }, { extra, rejectWithValue }) => {
        const { api } = extra

        try {
            const res = await api.get(`/location/latlng/${lat},${long}`)
            return Promise.resolve(res)
        } catch (err) {
            return rejectWithValue(err?.message || err?.error?.message || true)
        }
    }
)

export const fetchCities = createAsyncThunk(
    "selectCity/fetchCities",
    async (_arg, { extra, rejectWithValue, getState }) => {
        const { api } = extra
        const { benefitType = null, benefitId = null } = _arg
        try {
            const corporateDetails = getState().shellReducer.corporateDetails
            const corporateName = corporateDetails?._source || getCookie("affiliate-details")
            const res = await api.get(
                `/api/rbf/corporate/${corporateName}/cities?benefit_type=${benefitType}&benefit_id=${benefitId}`
            )
            return Promise.resolve(res.data)
        } catch (err) {
            return rejectWithValue(err?.message || err?.error?.message || true)
        }
    }
)

/**
 * @description Confirm Lab Test booking thunk
 */
export const managePatient = createAsyncThunk(
    "corporateLabsBookingPage/managePatient",
    async (_arg, { extra, rejectWithValue, getState }) => {
        try {
            const { corporateDetails: { _source: source } = {} } = getState().shellReducer
            const { api } = extra
            const { payload, patientId, type } = _arg
            const requestMethod = type === "update" ? "put" : "post"
            let apiUrl = `/api/rbf/corporate/${source}/beneficiaries/patient`
            apiUrl = type === "update" ? `${apiUrl}/${patientId}` : apiUrl
            const res = await api[requestMethod](apiUrl, payload)
            if (res) {
                return Promise.resolve(res.data)
            }
            return rejectWithValue(genericErrorMessage)
        } catch (error) {
            return rejectWithValue(error?.error?.message || genericErrorMessage)
        }
    }
)

export const createCart = createAsyncThunk(
    "corporateLabsBookingPage/createCart",
    async (_arg, { extra, getState, dispatch }) => {
        try {
            const { formData } = getState().corporateLabsBookingPageReducer
            const patientId = formData?.patientName?.id
            const city = formData?.patientCity.value
            const { api } = extra
            const { benefitId } = _arg
            const payload = {
                patient_id: patientId,
                city,
                types: "corporate", //type is static for corporate
                ...(benefitId !== null && { benefit_id: benefitId }),
            }

            const res = await api.post(`api/v4/carts/create`, payload)
            if (res) {
                return Promise.resolve(res.data)
            }
            throw new Error(genericErrorMessage)
        } catch (error) {
            dispatch(setSnackBarError(genericErrorMessage))
            setTimeout(() => {
                dispatch(setSnackBarError(""))
            }, 2000)
        }
    }
)

export const createCartSku = createAsyncThunk(
    "corporateLabsBookingPage/createCartSku",
    async (_arg, { extra, rejectWithValue, getState, dispatch }) => {
        try {
            const { formData } = getState().corporateLabsBookingPageReducer
            const packageId = formData?.patientLabTest?.id

            const { api } = extra
            const payload = {
                package_id: packageId,
                types: "corporate", //type is static for corporate
            }
            const apiUrl = `api/v4/carts/skus`
            const res = await api.post(apiUrl, payload)
            if (res) {
                return Promise.resolve(res.data)
            }
            dispatch(setSnackBarError(genericErrorMessage))
            setTimeout(() => {
                dispatch(setSnackBarError(""))
            }, 2000)
            return rejectWithValue(genericErrorMessage)
        } catch (error) {
            dispatch(setSnackBarError(genericErrorMessage))
            setTimeout(() => {
                dispatch(setSnackBarError(""))
            }, 2000)
            return rejectWithValue(error?.error?.message || genericErrorMessage)
        }
    }
)

export const cartPreference = createAsyncThunk(
    "corporateLabsBookingPage/cartPreference",
    async (args, { extra, rejectWithValue, dispatch }) => {
        try {
            const { api } = extra

            const apiUrl = `api/v4/carts/preferences`
            const res = await api["put"](apiUrl, { ...args, types: "corporate" })
            if (res) {
                dispatch(setAllowNextStep(true))
                return Promise.resolve(res.data)
            }
            dispatch(setAllowNextStep(false))
            dispatch(setDialogError(genericErrorMessage))
            return rejectWithValue(genericErrorMessage)
        } catch (error) {
            dispatch(setAllowNextStep(false))
            dispatch(
                setDialogError(error?.error?.message || error?.errors[0]?.message || genericErrorMessage)
            )
            return rejectWithValue(error?.error?.message || error?.errors[0]?.message || genericErrorMessage)
        }
    }
)

export const cartFinish = createAsyncThunk(
    "corporateLabsBookingPage/cartFinish",
    async (_args, { extra, rejectWithValue }) => {
        try {
            const { api } = extra

            const apiUrl = `api/v1/corporate/carts/finish`
            const res = await api.delete(apiUrl)
            if (res) {
                return Promise.resolve({ benefitId: res.data?.benefit_id })
            }
            return rejectWithValue(genericErrorMessage)
        } catch (error) {
            return rejectWithValue(error?.error?.message || genericErrorMessage)
        }
    }
)

/**
 * Thunk action to check the feasibility of a corporate lab booking package.
 *
 * @function
 * @name checkFeasibility
 * @param {string} _arg.packageId - The ID of the package to check feasibility for.
 * @returns {Promise<Object>} The response data from the API call.
 * @throws Will throw an error if the API call fails.
 */
export const checkFeasibility = createAsyncThunk(
    "corporateLabsBookingPage/checkFeasibility",
    async (_arg, { extra, rejectWithValue, getState, dispatch }) => {
        try {
            const { corporateDetails: { _source: corporateName } = {} } = getState().shellReducer
            const { packageId } = _arg
            const { api } = extra
            const res = await api.get(`/api/rbf/corporate/${corporateName}/feasibility-check/${packageId}`)

            res.packageId = packageId
            return Promise.resolve(res)
        } catch (error) {
            dispatch(setSnackBarError(error?.message || "Something went wrong. Please try again later"))
            setTimeout(() => {
                dispatch(setSnackBarError(""))
            }, 3000)
            return rejectWithValue(error?.message || "Something went wrong. Please try again later")
        }
    }
)

export const corporateLabsBookingPageReducer = corporateLabsBookingPageSlice.reducer

export const {
    setFormData,
    clearSectionData,
    resetForm,
    setAddressSelectionLoading,
    setFormError,
    setSnackBarError,
    setTestLocation,
    setCurrentStep,
    cleanPageError,
    clearSlotSelection,
    resetTestLocation,
    resetFeasbillity,
    setAllowNextStep,
    setDialogError,
} = corporateLabsBookingPageSlice.actions
